/**
 * WASC member-induction checklist — automation-first (see docs/INDUCTION_AUTOMATION.md).
 * Keep enum in packages/db/migrations/0025 + 0027 in sync for DB keys.
 * squad_drive_docs: legacy enum value only — not in INDUCTION_TASKS (see 0026).
 */

export type InductionTaskKey =
  | 'marshal_intake'
  | 'coach_signoff'
  | 'officers_notified'
  | 'xero_contact'
  | 'whatsapp_squad'
  | 'squad_drive_docs'
  | 'fastlane_parent'
  | 'fastlane_verified'
  | 'club_subscription_paid';

/** Keys stored in DB but hidden from checklist UI (enum retained). */
export const DEPRECATED_INDUCTION_TASK_KEYS = ['squad_drive_docs'] as const;

export type InductionTaskOwner = 'jo' | 'sue' | 'linda' | 'parent' | 'coach' | 'system';

export type InductionAutomationStatus = 'pending' | 'running' | 'done' | 'failed';

/** Diagram node styling — see docs/INDUCTION_AUTOMATION.md */
export type InductionNodeKind = 'automated' | 'manual' | 'human';

export interface InductionFlowFallback {
  label: string;
  description: string;
}

export interface InductionTaskDef {
  key: InductionTaskKey;
  label: string;
  description: string;
  owner: InductionTaskOwner;
  ownerLabel: string;
  integration:
    | 'marshal'
    | 'manual'
    | 'whatsapp_zero'
    | 'xero_api'
    | 'fastlane_manual';
  /** System completes when integration succeeds — no officer checkbox. */
  automated: boolean;
  /** Rare admin override on automated steps (default false). */
  manual_allowed: boolean;
  sortOrder: number;
  /** Committee flow diagram order (may match sortOrder). */
  flowOrder: number;
  /** Upstream steps for dependency arrows in InductionFlowDiagram (see INDUCTION_AUTOMATION.md). */
  dependsOn?: InductionTaskKey[];
  /** Node colour / icon in InductionFlowDiagram */
  nodeKind: InductionNodeKind;
  /** Dashed side branch when API path unavailable */
  fallback?: InductionFlowFallback;
}

export const INDUCTION_TASKS: InductionTaskDef[] = [
  {
    key: 'marshal_intake',
    label: 'In Marshal',
    description: 'Swimmer + guardian contact details in the roster (import or add).',
    owner: 'jo',
    ownerLabel: 'Jo',
    integration: 'marshal',
    automated: true,
    manual_allowed: false,
    sortOrder: 10,
    flowOrder: 10,
    nodeKind: 'automated',
  },
  {
    key: 'coach_signoff',
    label: 'Coach pool sign-off',
    description:
      'Head coach confirms pool assessment and allocates a squad — unlocks post-sign-off steps (Xero, WhatsApp, officer notify).',
    owner: 'coach',
    ownerLabel: 'Coach',
    integration: 'marshal',
    automated: false,
    manual_allowed: false,
    sortOrder: 15,
    flowOrder: 20,
    dependsOn: ['marshal_intake'],
    nodeKind: 'human',
  },
  {
    key: 'officers_notified',
    label: 'Officers notified',
    description: 'Linda, Sue, and Jo notified of induction status — email automation planned (M2).',
    owner: 'system',
    ownerLabel: 'Marshal',
    integration: 'marshal',
    automated: false,
    manual_allowed: false,
    sortOrder: 20,
    flowOrder: 30,
    dependsOn: ['coach_signoff'],
    nodeKind: 'manual',
  },
  {
    key: 'xero_contact',
    label: 'Xero contact sync',
    description: 'Billing contact upserted in Xero after coach sign-off, named Last (First) like Hardwick (Will), using the guardian email.',
    owner: 'system',
    ownerLabel: 'Marshal / Sue',
    integration: 'xero_api',
    automated: true,
    manual_allowed: false,
    sortOrder: 30,
    flowOrder: 31,
    dependsOn: ['coach_signoff'],
    nodeKind: 'automated',
    fallback: {
      label: 'Sue manual in Xero',
      description: 'Treasurer creates or updates the contact by hand if OAuth or sync fails.',
    },
  },
  {
    key: 'whatsapp_squad',
    label: 'WhatsApp squad group',
    description:
      'Guardian added to squad WhatsApp — Business API planned (M2). Use CSV export to add manually today.',
    owner: 'system',
    ownerLabel: 'Marshal / Linda',
    integration: 'whatsapp_zero',
    automated: false,
    manual_allowed: false,
    sortOrder: 40,
    flowOrder: 32,
    dependsOn: ['coach_signoff'],
    nodeKind: 'manual',
    fallback: {
      label: 'CSV export',
      description: 'Download guardian phones from Induction and add to the squad group manually.',
    },
  },
  {
    key: 'fastlane_parent',
    label: 'Parent started Fastlane',
    description: 'Parent has begun SNZ Fastlane registration (link sent; no API yet).',
    owner: 'parent',
    ownerLabel: 'Parent',
    integration: 'fastlane_manual',
    automated: false,
    manual_allowed: true,
    sortOrder: 60,
    flowOrder: 50,
    dependsOn: ['coach_signoff'],
    nodeKind: 'manual',
  },
  {
    key: 'fastlane_verified',
    label: 'Fastlane checked',
    description: 'Linda has verified the Fastlane profile is complete.',
    owner: 'linda',
    ownerLabel: 'Linda',
    integration: 'fastlane_manual',
    automated: false,
    manual_allowed: true,
    sortOrder: 70,
    flowOrder: 60,
    dependsOn: ['fastlane_parent'],
    nodeKind: 'manual',
  },
  {
    key: 'club_subscription_paid',
    label: 'Club subscription paid',
    description:
      'Club yearly subscription paid — Linda confirms manually today; Xero invoice webhook planned (M2).',
    owner: 'system',
    ownerLabel: 'Marshal / Linda',
    integration: 'xero_api',
    automated: false,
    manual_allowed: true,
    sortOrder: 80,
    flowOrder: 70,
    dependsOn: ['fastlane_verified', 'xero_contact'],
    nodeKind: 'manual',
    fallback: {
      label: 'Linda confirm',
      description: 'Membership officer marks paid when Fastlane / bank confirms payment.',
    },
  },
];

/** Tasks in committee flow-diagram order (excludes deprecated keys). */
export const INDUCTION_FLOW_TASKS = [...INDUCTION_TASKS].sort(
  (a, b) => a.flowOrder - b.flowOrder,
);

/** Linear spine before parallel fan-out (coach sign-off index). */
export const INDUCTION_FLOW_SPINE_KEYS: InductionTaskKey[] = ['marshal_intake', 'coach_signoff'];

/** Steps that run in parallel after coach sign-off (Marshal automations). */
export const INDUCTION_FLOW_PARALLEL_KEYS: InductionTaskKey[] = [
  'officers_notified',
  'xero_contact',
  'whatsapp_squad',
];

/** Committee flow diagram layers (top → bottom). */
export const INDUCTION_FLOW_LAYERS: InductionTaskKey[][] = [
  ['marshal_intake'],
  ['coach_signoff'],
  INDUCTION_FLOW_PARALLEL_KEYS,
  ['fastlane_parent'],
  ['fastlane_verified'],
  ['club_subscription_paid'],
];

/** Diagram connector style — primary spine vs cross-layer `dependsOn`. */
export type InductionFlowEdgeKind = 'primary' | 'dependency';

export interface InductionFlowEdge {
  from: InductionTaskKey;
  to: InductionTaskKey;
  kind: InductionFlowEdgeKind;
  /** Optional label on dashed dependency edges (e.g. Xero webhook). */
  label?: string;
}

/** Cross-layer edges rendered dashed (not the vertical spine). */
export const INDUCTION_FLOW_DEPENDENCY_EDGES: ReadonlyArray<{
  from: InductionTaskKey;
  to: InductionTaskKey;
  label?: string;
}> = [{ from: 'xero_contact', to: 'club_subscription_paid', label: 'webhook' }];

function inductionFlowEdgeKind(from: InductionTaskKey, to: InductionTaskKey): InductionFlowEdgeKind {
  return INDUCTION_FLOW_DEPENDENCY_EDGES.some((e) => e.from === from && e.to === to)
    ? 'dependency'
    : 'primary';
}

function inductionFlowEdgeLabel(from: InductionTaskKey, to: InductionTaskKey): string | undefined {
  return INDUCTION_FLOW_DEPENDENCY_EDGES.find((e) => e.from === from && e.to === to)?.label;
}

/** Directed edges for diagram: upstream `from` → downstream `to`. */
export function inductionFlowEdges(): InductionFlowEdge[] {
  const edges: InductionFlowEdge[] = [];
  const push = (from: InductionTaskKey, to: InductionTaskKey) => {
    if (!edges.some((e) => e.from === from && e.to === to)) {
      edges.push({
        from,
        to,
        kind: inductionFlowEdgeKind(from, to),
        label: inductionFlowEdgeLabel(from, to),
      });
    }
  };

  push('marshal_intake', 'coach_signoff');
  for (const key of INDUCTION_FLOW_PARALLEL_KEYS) push('coach_signoff', key);
  push('coach_signoff', 'fastlane_parent');
  push('fastlane_parent', 'fastlane_verified');

  for (const task of INDUCTION_TASKS) {
    for (const dep of task.dependsOn ?? []) push(dep, task.key);
  }
  return edges;
}

/** Active checklist keys (UI + progress). Excludes deprecated enum values. */
export const INDUCTION_TASK_KEYS = INDUCTION_TASKS.map((t) => t.key);

export const AUTOMATED_INDUCTION_TASK_KEYS = INDUCTION_TASKS.filter((t) => t.automated).map(
  (t) => t.key,
);

export { FASTLANE_PARENT_URL } from './fastlane';

/** Membership statuses that belong on the induction board (not active/archived). */
export const PENDING_INDUCTION_STATUSES = [
  'enquired',
  'pending_paperwork',
  'pending_payment',
  'pending_swim_assessment',
] as const;

/** Ordered induction pipeline used by People filters and bulk moves. */
export const INDUCTION_PIPELINE = [
  'enquired',
  'pending_paperwork',
  'pending_payment',
  'pending_swim_assessment',
  'active',
] as const;

export type InductionPipelineStatus = (typeof INDUCTION_PIPELINE)[number];

export function nextInductionPipelineStatus(current: string): InductionPipelineStatus | null {
  const idx = (INDUCTION_PIPELINE as readonly string[]).indexOf(current);
  if (idx < 0 || idx >= INDUCTION_PIPELINE.length - 1) return null;
  return INDUCTION_PIPELINE[idx + 1];
}

/** Shared status when every selected member is on the same induction step. */
export function sharedInductionStatus(statuses: readonly string[]): string | null {
  if (statuses.length === 0) return null;
  const first = statuses[0];
  return statuses.every((s) => s === first) ? first : null;
}

export type PendingInductionStatus = (typeof PENDING_INDUCTION_STATUSES)[number];

export const OWNER_LABELS: Record<InductionTaskOwner, string> = {
  jo: 'Jo',
  sue: 'Sue',
  linda: 'Linda',
  parent: 'Parent',
  coach: 'Coach',
  system: 'Marshal',
};

export function inductionTaskDef(key: InductionTaskKey): InductionTaskDef | undefined {
  return INDUCTION_TASKS.find((t) => t.key === key);
}
